const touches = (a, b) => {
  // has horizontal gap
  if (a.left > b.right || b.left > a.right) return false;

  // has vertical gap
  if (a.top > b.bottom || b.top > a.bottom) return false;

  // vertical overlap more than 60% of b
  const overlapHeight = Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top);
  const bHeight = b.bottom - b.top;
  return overlapHeight / bHeight > 0.6;
};

const lastWordIndex = (str, from) =>
  Array.from(str.substr(0, from).matchAll(new RegExp('(?<=\\w)[^\\w]', 'g')))?.pop()?.index || -1;

export const rectRelativeTo = (el, referenceEl) => {
  const r = el.getBoundingClientRect();
  const ref = referenceEl.getBoundingClientRect();
  return {
    left: r.left - ref.left,
    right: r.right - ref.left,
    top: r.top - ref.top,
    bottom: r.bottom - ref.top,
  };
};

/** Same vertical tolerance as `rectangleSelect` same-line check (glyph runs differ by a few px). */
const SAME_LINE_MID_Y_TOL = 14;

/**
 * Expand `rectangleSelect` hits to all `span[role=presentation]` on the same text row.
 * A tight box around one word makes a small union rect — `touches(union, span)` then drops
 * neighbours on the line (logs: `tokenCount:1`, `pos:0` for ESB). Use **mid-Y ± tol** vs each
 * span's vertical centre instead (same idea as same-line in `rectangleSelect`).
 */
export function collectPresentationSpansSameRow(referenceEl, hitSpans, textLayerRoot) {
  if (!referenceEl || !hitSpans?.length || !textLayerRoot) {
    return hitSpans || [];
  }
  let midSum = 0;
  hitSpans.forEach(el => {
    const r = rectRelativeTo(el, referenceEl);
    midSum += (r.top + r.bottom) / 2;
  });
  const midY = midSum / hitSpans.length;
  const out = [];
  textLayerRoot.querySelectorAll('span[role="presentation"]').forEach(el => {
    const r = rectRelativeTo(el, referenceEl);
    const yc = (r.top + r.bottom) / 2;
    if (Math.abs(yc - midY) <= SAME_LINE_MID_Y_TOL) {
      out.push(el);
    }
  });
  out.sort((a, b) => {
    const ra = rectRelativeTo(a, referenceEl);
    const rb = rectRelativeTo(b, referenceEl);
    return ra.left - rb.left || ra.top - rb.top;
  });
  return out.length ? out : [...hitSpans];
}

/**
 * @param {NodeList|HTMLElement[]} inputElements
 * @param {{left:number,right:number,top:number,bottom:number}} selectionRectangle
 * @param {HTMLElement} [referenceEl] Overlay canvas: use getBoundingClientRect-relative geometry (pdfjs 4 text layout).
 */
export const rectangleSelect = (inputElements, selectionRectangle, referenceEl) => {
  const elements = [];
  inputElements.forEach(element => {
    const eRect = referenceEl
      ? rectRelativeTo(element, referenceEl)
      : {
          left: element.offsetLeft,
          right: element.offsetLeft + element.offsetWidth,
          top: element.offsetTop,
          bottom: element.offsetTop + element.offsetHeight,
        };
    if (touches(selectionRectangle, eRect)) {
      elements.push(element);
    }
  });
  const selection = window.getSelection();
  selection.removeAllRanges();
  if (elements.length) {
    const range = document.createRange();
    let offsetStart = 0;
    const startEl = elements[0];
    const endEl = elements[elements.length - 1];
    const addX = referenceEl ? 0 : startEl.offsetLeft - startEl.getBoundingClientRect().left;
    const startNode = startEl.firstChild;
    const endNode = endEl.firstChild;
    if (!startNode || !endNode || startNode.nodeType !== Node.TEXT_NODE) {
      return elements;
    }
    range.setStart(startNode, offsetStart);
    let offsetEnd = endEl.innerText.length;
    range.setEnd(endNode, offsetEnd);
    // pdfjs 4 uses per-glyph positioning; "same line" spans often differ by >3px in top.
    const sameLineRef =
      referenceEl &&
      (() => {
        const a = rectRelativeTo(startEl, referenceEl);
        const b = rectRelativeTo(endEl, referenceEl);
        const midA = (a.top + a.bottom) / 2;
        const midB = (b.top + b.bottom) / 2;
        return Math.abs(midA - midB) <= 14;
      })();
    const sameLineLegacy = !referenceEl && Math.abs(startEl.offsetTop - endEl.offsetTop) <= 3;
    if (!sameLineRef && !sameLineLegacy) {
      selection.addRange(range);
      throw new Error('You selected multiple lines');
    }
    const refRect = referenceEl ? referenceEl.getBoundingClientRect() : null;
    const selLeft = () =>
      referenceEl
        ? range.getBoundingClientRect().left - refRect.left
        : range.getBoundingClientRect().left + addX;
    const selRight = () =>
      referenceEl
        ? range.getBoundingClientRect().right - refRect.left
        : range.getBoundingClientRect().right + addX;
    while (selLeft() < selectionRectangle.left && offsetStart < startEl.innerText.length) {
      const nextWord = startEl.innerText.substr(offsetStart).match(/[^\w]\w/)?.index;
      offsetStart =
        typeof nextWord === 'number' ? offsetStart + nextWord + 1 : startEl.innerText.length;
      range.setStart(startNode, offsetStart);
    }
    while (selRight() > selectionRectangle.right && offsetEnd > 0) {
      const prevWord = lastWordIndex(endEl.innerText, offsetEnd);
      offsetEnd = prevWord > 0 ? prevWord : 0;
      range.setEnd(endNode, offsetEnd);
    }
    selection.addRange(range);
  }
  return elements;
};

/**
 * Maps a drag in **canvas bitmap pixel** coordinates (see `HrBotConfig` `getMouseCoords`) to the
 * `{ left, top, right, bottom }` rectangle passed to `rectangleSelect(..., canvas)` — CSS pixels
 * relative to the canvas border box. Single source of truth with {@link selectPresentationSpansInCanvasRect}.
 */
export function selectionRectCanvasClientFromInternalDrag(canvasEl, x1, y1, x2, y2) {
  if (!canvasEl) {
    return { left: 0, top: 0, right: 0, bottom: 0 };
  }
  const left = Math.min(x1, x2);
  const right = Math.max(x1, x2);
  const top = Math.min(y1, y2);
  const bottom = Math.max(y1, y2);
  const bounds = canvasEl.getBoundingClientRect();
  const cw = canvasEl.width;
  const ch = canvasEl.height;
  return {
    left: (left * bounds.width) / cw,
    top: (top * bounds.height) / ch,
    right: (right * bounds.width) / cw,
    bottom: (bottom * bounds.height) / ch,
  };
}

/**
 * Ghost box in placement-overlay local coordinates (same units as `offsetX` / `offsetY` on the overlay).
 * Converts to the same canvas-client space as {@link selectionRectCanvasClientFromInternalDrag}.
 */
export function selectionRectCanvasClientFromOverlayGhost(canvasEl, overlayEl, ghostOverlayLocal) {
  if (!canvasEl || !overlayEl || !ghostOverlayLocal) {
    return { left: 0, top: 0, right: 0, bottom: 0 };
  }
  const oRect = overlayEl.getBoundingClientRect();
  const cRect = canvasEl.getBoundingClientRect();
  return {
    left: oRect.left + ghostOverlayLocal.left - cRect.left,
    top: oRect.top + ghostOverlayLocal.top - cRect.top,
    right: oRect.left + ghostOverlayLocal.right - cRect.left,
    bottom: oRect.top + ghostOverlayLocal.bottom - cRect.top,
  };
}

/**
 * Shared `HrBotConfig` / template-editor path: `rectangleSelect` with **canvas** (or same-sized ref)
 * as `referenceEl`, trimmed `getSelection().toString()`, then {@link collectPresentationSpansSameRow}.
 */
export function selectPresentationSpansInCanvasRect(
  canvasEl,
  textLayerRoot,
  selectionRectCanvasClient,
) {
  const cleared = () => ({
    multiline: false,
    noHit: true,
    selectedText: '',
    spansForPos: [],
    selected: [],
  });
  if (!canvasEl || !textLayerRoot) {
    return cleared();
  }
  const spanNodes = textLayerRoot.querySelectorAll('span[role="presentation"]');
  let selected;
  try {
    selected = rectangleSelect(spanNodes, selectionRectCanvasClient, canvasEl);
  } catch (e) {
    window.getSelection?.()?.removeAllRanges?.();
    if (e?.message === 'You selected multiple lines') {
      return { multiline: true, noHit: false, selectedText: '', spansForPos: [], selected: [] };
    }
    throw e;
  }
  if (!selected?.length) {
    window.getSelection?.()?.removeAllRanges?.();
    return cleared();
  }
  const selectedText = (window.getSelection?.()?.toString?.() || '').trim();
  window.getSelection?.()?.removeAllRanges?.();
  const spansForPos = collectPresentationSpansSameRow(canvasEl, selected, textLayerRoot);
  return { multiline: false, noHit: false, selectedText, spansForPos, selected };
}
